import { useJsonForms, withJsonFormsArrayControlProps } from "@jsonforms/react";
import { Button, Font, InputItem, TextArea } from "pepsico-ds";
import { SingleImageUploadComponent } from "../fileUpload/SingleImageUploadControl";
import "./checkInGeoTable.scss";

const CheckInGeoTableComponent = (props) => {
  const { handleChange, path, required, visible, data, rootData } = props;

  // Access full form data from context as fallback
  const ctx = useJsonForms();
  const fullData = ctx?.core?.data || {};

  // Use rootData if available, otherwise fall back to context data
  const requireGeolocation =
    rootData?.require_geolocation ?? fullData?.require_geolocation ?? false;

  const addLocation = () => {
    const newLocation = {
      name: "",
      image: "",
      description: "",
      referral_id: "",
      latitude: 0,
      longitude: 0,
    };
    handleChange(path, [...(data || []), newLocation]);
  };

  const isLastRowValid = () => {
    if (!data || data.length === 0) return true;
    const last = data[data.length - 1];

    if (!last.referral_id?.trim()) return false;
    if (!last.name?.trim()) return false;

    if (requireGeolocation) {
      return (
        typeof last.latitude === "number" &&
        !isNaN(last.latitude) &&
        typeof last.longitude === "number" &&
        !isNaN(last.longitude)
      );
    }

    return true;
  };

  const updateLocation = (index, key, value) => {
    const newData = [...data];
    newData[index][key] = value;
    handleChange(path, newData);
  };

  const removeLocation = (index) => {
    const newData = [...data];
    newData.splice(index, 1);
    handleChange(path, newData);
  };

  const handleImageChange = (index, newValue) => {
    updateLocation(index, "image", newValue);
  };

  if (!visible) {
    return null;
  }

  return (
    <div className="check-in-geo-table-wrapper">
      <div className="check-in-geo-table">
        {data?.map((location, index) => (
          <div key={index} className="location-card">
            <div className="location-card-header">
              <Font size="sm" weight="semibold">
                Check-in location
              </Font>
              <Button
                onClick={() => removeLocation(index)}
                variant="tertiary"
                size="small"
              >
                Remove
              </Button>
            </div>

            <div className="location-card-body">
              <div className="field-group">
                <Font size="sm" weight="medium" className="field-label">
                  Name*
                </Font>
                <InputItem
                  value={location.name || ""}
                  onChange={(e) => updateLocation(index, "name", e.target.value)}
                  fullWidth
                  error={!location.name?.trim()}
                  helperText={!location.name?.trim() ? "Required" : ""}
                  placeholder="Type"
                />
              </div>

              <div className="field-group">
                <SingleImageUploadComponent
                  data={location.image || ""}
                  handleChange={(path, value) => handleImageChange(index, value)}
                  path={`${path}.${index}.image`}
                  label="Image"
                  schema={{
                    maxWidth: 2000,
                    maxHeight: 2000,
                    maxSize: 1000,
                  }}
                  uischema={{
                    accept: ".jpg,.jpeg,.png",
                  }}
                  required={false}
                  visible={true}
                  enabled={requireGeolocation}
                />
              </div>

              <div className="field-group">
                <Font size="sm" weight="medium" className="field-label">
                  Description
                </Font>
                <TextArea
                  value={location.description || ""}
                  onChange={(e) =>
                    updateLocation(index, "description", e.target.value)
                  }
                  disabled={!requireGeolocation}
                  placeholder="Type here"
                  rows={3}
                />
              </div>

              <div className="field-group-row">
                <div className="field-group">
                  <Font size="sm" weight="medium" className="field-label">
                    Referral ID*
                  </Font>
                  <InputItem
                    value={location.referral_id || ""}
                    onChange={(e) =>
                      updateLocation(index, "referral_id", e.target.value)
                    }
                    fullWidth
                    required={required}
                    error={!location.referral_id?.trim()}
                    helperText={!location.referral_id?.trim() ? "Required" : ""}
                    placeholder="Type"
                  />
                </div>

                <div className="field-group">
                  <Font size="sm" weight="medium" className="field-label">
                    Latitude{requireGeolocation ? "*" : ""}
                  </Font>
                  <InputItem
                    type="number"
                    disabled={!requireGeolocation}
                    value={location.latitude || ""}
                    onChange={(e) =>
                      updateLocation(
                        index,
                        "latitude",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    fullWidth
                    required={requireGeolocation && required}
                    error={requireGeolocation && isNaN(location.latitude)}
                    placeholder="Type"
                  />
                </div>

                <div className="field-group">
                  <Font size="sm" weight="medium" className="field-label">
                    Longitude{requireGeolocation ? "*" : ""}
                  </Font>
                  <InputItem
                    type="number"
                    disabled={!requireGeolocation}
                    value={location.longitude || ""}
                    onChange={(e) =>
                      updateLocation(
                        index,
                        "longitude",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    fullWidth
                    required={requireGeolocation && required}
                    error={requireGeolocation && isNaN(location.longitude)}
                    placeholder="Type"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button
        onClick={addLocation}
        className="add-location-btn"
        disabled={!isLastRowValid()}
        size="small"
      >
        Add new location
      </Button>
    </div>
  );
};

export const CheckInGeoTableController = withJsonFormsArrayControlProps(
  CheckInGeoTableComponent
);
